import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Divider, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import ModalWithLoader, {
  RequestStatus
} from '../../../../../Tools/ModalWithLoader/ModalWithLoader';
import { useInterventionCreation } from '../../context/interventionCreationContext';
import { apiErrorMessage } from './apiError';
import { buildCherryPickPayload, buildCherryPickUrl } from './pdsImport';
import { buildPhoneCountryOptions } from './phoneCountries';
import { UnitEntryDraft, isContactPhoneRequired, isUnitEntryValid } from './unitEntry';

const EMPTY_DRAFT: UnitEntryDraft = {
  equipmentId: '',
  contactName: '',
  contactPhone: ''
};

const DEFAULT_COUNTRY_CODE = 'FR';

/**
 * Durée d'affichage du message de succès avant fermeture automatique, alignée sur le
 * `timeout` par défaut de `ModalWithLoader`.
 */
const SUCCESS_DISPLAY_MS = 1500;

export default function UnitEntryTab() {
  const { t, i18n } = useTranslation();
  const { state, dispatch } = useInterventionCreation();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState<UnitEntryDraft>(EMPTY_DRAFT);
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);

  const countryOptions = useMemo(
    () => buildPhoneCountryOptions(t, i18n.language),
    [t, i18n.language]
  );
  const selectedCountry = countryOptions.find((country) => country.code === countryCode);

  const phoneRequired = isContactPhoneRequired(draft);
  const phoneMissing = phoneRequired && draft.contactPhone.trim() === '';

  const updateDraft = (field: keyof UnitEntryDraft, value: string) =>
    setDraft((previous) => ({ ...previous, [field]: value }));

  const reset = () => {
    setDraft(EMPTY_DRAFT);
    setCountryCode(DEFAULT_COUNTRY_CODE);
  };

  /** Renvoie l'identifiant de la demande créée, `null` quand l'API ne le donne pas. */
  const addServicePoint = useMutation<number | null, unknown, void>({
    mutationFn: async () => {
      if (state.clientId === null || selectedCountry === undefined) {
        return null;
      }

      // Contrairement aux imports fichier et copier/coller, l'API attend du JSON ici.
      const response = await axios.post(
        buildCherryPickUrl(state.clientId),
        buildCherryPickPayload(draft, selectedCountry.dialCode)
      );

      // L'API renvoie la demande sérialisée ; l'ajout reste un succès si elle se tait,
      // seule la mise en valeur de la ligne est alors perdue.
      return typeof response.data?.id === 'number' ? response.data.id : null;
    },
    onSuccess: (createdId) => {
      reset();
      // Une saisie unitaire complète le dernier ajout au lieu de le remplacer : l'utilisateur
      // en enchaîne plusieurs sans quitter la modale.
      if (createdId !== null) {
        dispatch({ type: 'addNewRequest', id: createdId });
      }
      // `pds/to-select/` n'est plus lu par cet écran : rien à invalider de ce côté.
      queryClient.invalidateQueries(['exocet-pds-to-qualify']);
    }
  });

  const { isSuccess, reset: resetMutation } = addServicePoint;

  /**
   * Fermeture automatique après un ajout réussi.
   *
   * `ModalWithLoader` n'arme son propre minuteur que lorsque `openModal` est déjà faux :
   * il achève une fermeture, il ne la déclenche pas. Le déclenchement revient donc à
   * l'appelant. Réinitialiser la mutation ramène `requestStatus` à `null`, donc
   * `openModal` à faux.
   *
   * Seul le succès se ferme seul : une erreur doit rester lisible.
   */
  useEffect(() => {
    if (!isSuccess) {
      return;
    }

    const timer = setTimeout(resetMutation, SUCCESS_DISPLAY_MS);

    return () => clearTimeout(timer);
  }, [isSuccess, resetMutation]);

  const failureReason = apiErrorMessage(addServicePoint.error);

  const requestStatus: RequestStatus = addServicePoint.isLoading
    ? 'pending'
    : addServicePoint.isError
      ? 'error'
      : addServicePoint.isSuccess
        ? 'success'
        : null;

  const canSubmit =
    isUnitEntryValid(draft) && state.clientId !== null && !addServicePoint.isLoading;

  return (
    <>
      <ModalWithLoader
        openModal={requestStatus !== null}
        status={requestStatus}
        // Le nom et non l'infinitif : `ModalWithLoader` concatène cette valeur, ce qui
        // donnerait « Ajouter réalisé avec succès ». Le bouton, lui, garde `add`.
        action={t('interventionCreation.addServicePoints.unitEntry.addAction')}
        customErrorMessage={
          <Stack spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={500}>
              {t('interventionCreation.addServicePoints.unitEntry.addError')}
            </Typography>
            {/* Le motif du refus vient de l'API : « déjà sélectionné pour une création
                d'intervention » n'est devinable par aucun libellé générique. */}
            {failureReason !== null && (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {failureReason}
              </Typography>
            )}
          </Stack>
        }
        onClose={() => addServicePoint.reset()}
        onSuccess={() => addServicePoint.reset()}
      />

      <Stack spacing={4}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
          divider={
            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: 'none', md: 'block' } }}
            />
          }
        >
          <Stack spacing={1} sx={{ flex: 1 }}>
            <Typography variant="body2" component="label" htmlFor="unit-equipment">
              {`${t('interventionCreation.addServicePoints.unitEntry.equipment')} *`}
            </Typography>
            <TextField
              id="unit-equipment"
              fullWidth
              size="small"
              value={draft.equipmentId}
              onChange={(event) => updateDraft('equipmentId', event.target.value)}
              placeholder={t(
                'interventionCreation.addServicePoints.unitEntry.equipmentPlaceholder'
              )}
            />
          </Stack>

          <Stack spacing={2.5} sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('interventionCreation.addServicePoints.unitEntry.contact')}
            </Typography>

            <Stack spacing={1}>
              <Typography variant="body2" component="label" htmlFor="unit-contact-name">
                {t('interventionCreation.addServicePoints.unitEntry.name')}
              </Typography>
              <TextField
                id="unit-contact-name"
                fullWidth
                size="small"
                value={draft.contactName}
                onChange={(event) => updateDraft('contactName', event.target.value)}
                placeholder={t('interventionCreation.addServicePoints.unitEntry.namePlaceholder')}
              />
            </Stack>

            <Stack spacing={1}>
              <Typography variant="body2" component="label" htmlFor="unit-contact-phone">
                {`${t('interventionCreation.addServicePoints.unitEntry.phone')}${
                  phoneRequired ? ' *' : ''
                }`}
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <TextField
                  select
                  size="small"
                  sx={{ minWidth: 170 }}
                  value={countryCode}
                  onChange={(event) => setCountryCode(event.target.value)}
                  inputProps={{
                    'aria-label': t('interventionCreation.addServicePoints.unitEntry.countryCode')
                  }}
                >
                  {countryOptions.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  id="unit-contact-phone"
                  fullWidth
                  size="small"
                  value={draft.contactPhone}
                  onChange={(event) => updateDraft('contactPhone', event.target.value)}
                  placeholder={t(
                    'interventionCreation.addServicePoints.unitEntry.phonePlaceholder'
                  )}
                  error={phoneMissing}
                  helperText={
                    phoneMissing
                      ? t('interventionCreation.addServicePoints.unitEntry.phoneRequired')
                      : ''
                  }
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={reset}>
            {t('interventionCreation.addServicePoints.unitEntry.reset')}
          </Button>
          <Button
            variant="contained"
            disabled={!canSubmit}
            onClick={() => addServicePoint.mutate()}
          >
            {t('interventionCreation.addServicePoints.unitEntry.add')}
          </Button>
        </Box>
      </Stack>
    </>
  );
}
